import React, { useEffect, useMemo, useRef } from 'react';

import {
  FlatTable,
  FlatTableBody,
  FlatTableHead,
  FlatTableRow,
  FlatTableHeader,
  FlatTableRowHeader
} from 'carbon-react/lib/components/flat-table';
import { FormattedDate, FormattedMessage } from '@futrli/localisation';

import { NonFinancialDriverTreeNode } from '../../types';
import { forceLocalDate } from '../../../utils/dates';
import { DriverRow } from './driver-row';
import { DriversGridStyles } from './drivers-grid-styles';
import { DriverDragHandlers } from '../../hooks/useDriversDragDrop/useDriversDragDrop';
import * as styles from './drivers-data-grid.scss';
import { DriverGroupRow } from './driver-group-row';
import { filterTreeByName } from './utils';

const DATE_COLUMN_WIDTH = 120;
const DATA_GRID_ELEMENT = 'drivers-data-grid-scroll-container';

interface Props {
  tree: NonFinancialDriverTreeNode[];
  columns: string[];
  startColumn: string;
  searchText: string;
  isReordering?: boolean;
  isUpdating?: boolean;
  drag: DriverDragHandlers;
  flatData?: NonFinancialDriverTreeNode[];
}

export const DriversDataGrid = ({
  tree,
  columns,
  startColumn,
  searchText,
  isReordering,
  isUpdating,
  drag,
  flatData
}: Props) => {
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const filteredTree = useMemo(() => {
    if (isReordering || !searchText) {
      return tree;
    }

    return filterTreeByName(tree, searchText.toLowerCase());
  }, [tree, searchText, isReordering]);

  useEffect(() => {
    const startColumnIndex = columns.findIndex(column => column === startColumn);
    if (startColumnIndex < 0) {
      return;
    }

    const scrollContainer = gridContainerRef.current?.querySelector<HTMLElement>(
      `[data-element="${DATA_GRID_ELEMENT}"]`
    );
    scrollContainer?.scrollTo({ left: startColumnIndex * DATE_COLUMN_WIDTH });
  }, [columns, startColumn]);

  return (
    <div ref={gridContainerRef} className={styles.gridContainer}>
      <DriversGridStyles />
      <FlatTable data-element={DATA_GRID_ELEMENT} hasStickyHead>
        <FlatTableHead>
          <FlatTableRow>
            <FlatTableRowHeader width={300}>
              <FormattedMessage id="common.name" defaultMessage="Name" />
            </FlatTableRowHeader>
            {columns.map(column => (
              <FlatTableHeader key={column} width={DATE_COLUMN_WIDTH}>
                <FormattedDate value={forceLocalDate(column)} month="short" year="2-digit" />
              </FlatTableHeader>
            ))}
            <FlatTableHeader width={130}>
              <FormattedMessage id="predict.actions" defaultMessage="Actions" />
            </FlatTableHeader>
          </FlatTableRow>
        </FlatTableHead>
        <FlatTableBody>
          {filteredTree.map(row => {
            const flatIndex = flatData?.findIndex(n => n.id === row.id) || 0;
            if (row.isGroup) {
              return (
                <DriverGroupRow
                  key={row.id}
                  row={row}
                  columns={columns}
                  isReordering={isReordering}
                  isUpdating={isUpdating}
                  drag={drag}
                  index={flatIndex}
                  flatData={flatData}
                />
              );
            }
            return (
              <DriverRow
                key={row.id}
                row={row}
                columns={columns}
                isReordering={isReordering}
                isUpdating={isUpdating}
                drag={drag}
                index={flatIndex}
              />
            );
          })}
        </FlatTableBody>
      </FlatTable>
    </div>
  );
};
